import { useState, type FormEvent } from "react";
import { asDecimalString, Money, type InvoiceOrBillStatus, type InvoiceOrBillType } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn, usePagination, useSortableData } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createInvoice, listInvoices } from "../../api/invoicesApi";
import type { InvoiceOrBillOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { BranchSelect } from "../../components/BranchSelect";
import { listCustomers } from "../../api/customersApi";

const STATUS_FILTERS: (InvoiceOrBillStatus | "")[] = ["", "UNPAID", "PARTIAL", "PAID", "OVERDUE"];
const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

export function InvoicesPage() {
  const { ensureFreshToken } = useAuth();
  const [statusFilter, setStatusFilter] = useState<InvoiceOrBillStatus | "">("");
  const invoicesState = useAsyncData(
    () => ensureFreshToken().then((token) => listInvoices(token, statusFilter || undefined)),
    [statusFilter],
  );

  const { sortedData, sort, requestSort } = useSortableData<InvoiceOrBillOut, keyof InvoiceOrBillOut>(
    invoicesState.data ?? [],
    (row, key) => String(row[key]),
  );
  const { pageData, pagination, setPage } = usePagination(sortedData, 25);

  const [type, setType] = useState<InvoiceOrBillType>("CUSTOMER_INVOICE");
  const [contactName, setContactName] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const customersState = useAsyncData(() => ensureFreshToken().then((token) => listCustomers(token)), []);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createInvoice(token, {
        branch_id: branchId || null,
        type,
        contact_name: contactName,
        amount_due: asDecimalString(amountDue),
        due_date: dueDate,
        customer_id: customerId || undefined,
      });
      setContactName("");
      setAmountDue("");
      setDueDate("");
      setCustomerId("");
      invoicesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create invoice/bill.");
    } finally {
      setSubmitting(false);
    }
  }

  const customerById = new Map((customersState.data ?? []).map((c) => [c.id, c]));

  const columns: DataTableColumn<InvoiceOrBillOut, keyof InvoiceOrBillOut>[] = [
    { key: "type", header: "Type", sortable: true, render: (r) => (r.type === "CUSTOMER_INVOICE" ? "Invoice" : "Bill") },
    { key: "contact_name", header: "Contact", sortable: true, render: (r) => r.contact_name },
    {
      key: "customer_id",
      header: "Customer",
      render: (r) => (r.customer_id ? customerById.get(r.customer_id)?.name ?? <span className="muted">{r.customer_id.slice(0, 8)}</span> : "—"),
    },
    { key: "amount_due", header: "Amount Due", sortable: true, align: "right", render: (r) => Money.format(r.amount_due, TENANT_CURRENCY_FALLBACK) },
    { key: "amount_paid", header: "Amount Paid", sortable: true, align: "right", render: (r) => Money.format(r.amount_paid, TENANT_CURRENCY_FALLBACK) },
    { key: "due_date", header: "Due Date", sortable: true, render: (r) => r.due_date },
    { key: "status", header: "Status", sortable: true, render: (r) => r.status },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Invoices &amp; Bills</h1>
          <p className="page__subtitle">Customer invoices and vendor bills.</p>
        </div>
      </div>

      {invoicesState.error ? <StatusBanner kind="error">{invoicesState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">New invoice / bill</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="inv-type">
                Type
              </label>
              <select id="inv-type" className="sl-input" value={type} onChange={(e) => setType(e.target.value as InvoiceOrBillType)}>
                <option value="CUSTOMER_INVOICE">Customer Invoice</option>
                <option value="VENDOR_BILL">Vendor Bill</option>
              </select>
            </div>
            <Input label="Contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} required />
            <Input
              label="Amount due"
              inputMode="decimal"
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
              placeholder="0.00"
              required
            />
            <Input label="Due date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
            <BranchSelect id="inv-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="Enterprise-wide (no branch)" />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="inv-customer">
                Customer (optional)
              </label>
              <select id="inv-customer" className="sl-input" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">No customer on file (walk-in / free text contact only)</option>
                {(customersState.data ?? []).map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="status-filter">
              Status
            </label>
            <select
              id="status-filter"
              className="sl-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InvoiceOrBillStatus | "")}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
          </div>
        </div>
        {invoicesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={pageData}
            getRowId={(row) => row.id}
            sort={sort}
            onSortChange={requestSort}
            pagination={pagination}
            onPageChange={setPage}
            emptyMessage="No invoices/bills found."
            caption="Invoices & Bills"
          />
        )}
      </div>
    </div>
  );
}
